import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import Collapse from "@mui/material/Collapse";

import Box from "components/common/Box";
import Stack from "components/common/Stack";
import Text from "components/common/Text";
import Button from "components/common/Button";
import IconButton from "components/common/IconButton";
import AllInOneFooter from "components/allInOneDashboard/AllInOneFooter";
import PrimePricingSection from "components/Product-Info-Pages/Prime/PrimePricingSection";
import { UNIFIED_DASHBOARD_PATH } from "components/allInOneDashboard/constants/productRouteMap";
import { config } from "config/index";
import { thirdpartyApis } from "stores/thirdparty/thirdpartyApis";
import { thirdpartyParsers } from "stores/thirdparty/thirdpartyParsers";
import { jarvisPrimeOnboardingActions } from "stores/jarvisPrime/jarvisPrimeOnboarding/jarvisPrimeOnboardingActions";
import { onboardingActions } from "stores/onboarding/onboardingActions";
import { localStore } from "stores/localStorage";

const ONBOARDING_PATH = "/jarvis-prime/onboarding/overview";
const GOLD = "#D0A135";
const GOLD_SOFT = "#F6D280";

const img = (name) =>
  `/images/prime-product-info/${encodeURIComponent(name)}`;

const ICONS = {
  crown: img("prime-hero-crown.png"),
  frame: img("Frame.svg"),
  frameGold: img("Frame-gold.svg"),
  check: img("check-tick.svg"),
  optimization: img("optimization (1) 1.svg"),
  folder: img("folder 1.svg"),
  dashboard: img("dashboard 1.svg"),
  omnichannel: img("omnichannel 1.svg"),
  idea: img("idea 1.svg"),
  ideaAlt: img("idea-2.svg"),
  robustness: img("robustness 1.svg"),
  loading: img("loading 1.svg"),
  stochastic: img("stochastic_1 1.svg"),
  support: img("24-hours-support 1.svg"),
  shield: img("hero-shield.svg"),
  target: img("hero-target.svg"),
  chartSpline: img("hero-chart.svg"),
  forYouDot: img("for-you-dot.svg"),
};

const HERO_BENEFITS = [
  { icon: ICONS.shield, label: "Expert Portfolio Management" },
  { icon: ICONS.target, label: "Tailored for high net worth investors" },
  { icon: ICONS.chartSpline, label: "Long term wealth creation" },
];

const HERO_SUBHEAD_LINES = [
  "Personalized portfolios for HNIs & UHNIs. Invest with confidence,",
  "backed by Proprietary AI-driven research, continuous monitoring,",
  "and disciplined portfolio management to generate alpha.",
];
const HERO_SUBHEAD = HERO_SUBHEAD_LINES.join(" ");
const HERO_GRADIENT =
  "linear-gradient(90deg, #19140B 0%, #19120B 55%, #4B311B 100%)";
const HERO_CTA = "linear-gradient(270deg, #E8A50C 0.11%, #AE7B06 99.89%)";


const FEATURES = [
  {
    icon: ICONS.optimization,
    text: "Analyzes 10Mn+ data parameters to identify high potential investment opportunity.",
    gold: false,
  },
  {
    icon: ICONS.folder,
    text: "Uses 10 years of fundamental data and 18 years of technical data for comprehensive stock analysis",
    gold: false,
  },
  {
    icon: ICONS.dashboard,
    text: "Leverages 3 proprietary sentiment engines to understand market trends and investor sentiment",
    gold: false,
  },
  {
    icon: ICONS.omnichannel,
    text: "Designed for HNIs & UHNIs seeking a professionally managed equity portfolio",
    gold: false,
  },
  {
    icon: ICONS.idea,
    text: "Create and optimizes your portfolio with AI driven stock selection and allocation",
  },
  {
    icon: ICONS.robustness,
    text: "Actively monitors risk using a robust risk management engine",
  },
  {
    icon: ICONS.loading,
    text: "Automatically rebalances your portfolio as market conditions change",
  },
  {
    icon: ICONS.stochastic,
    text: "Includes a Market Exit Indicator for partial or complete exits during market corrections or crashes",
  },
  {
    icon: ICONS.support,
    text: "Ensures your investments remain aligned with evolving market conditions through 24 x 7 monitoring and AI driven decision making",
  },
];

const FOR_YOU_CARDS = [
  { label: "Personalized", description: "Value personalized portfolio management." },
  { label: "AI Insights", description: "Trust AI powered research and insights." },
  { label: "Low Effort", description: "Prefer investing without daily market monitoring." },
  { label: "Long Term", description: "Focus on long term wealth creation" },
  { label: "Eligibility", description: "Eligible with ₹15 lakh+ investable assets." },
  { label: "Bespoke", description: "Expect bespoke, not generic, portfolios." },
];

const QUICK_FACTS = [
  { label: "Investor", value: "HNIs & UHNIs" },
  { label: "Minimum Capital", value: "₹25 Lakhs" },
  { label: "Monitoring", value: "24 x 7" },
  { label: "Horizon", value: "1 -5 Years" },
  { label: "Monitoring", value: "Actively" },
  { label: "Execution", value: "2 Click" },
];

const Bold = ({ children }) => (
  <Box component="span" sx={{ fontWeight: 600 }}>
    {children}
  </Box>
);

const MOBILE_ABOUT_ITEMS = [
  {
    icon: ICONS.frameGold,
    text: (
      <>
        <Bold>AI engine live</Bold> in Indian Market since <Bold>2016</Bold>
      </>
    ),
  },
  {
    icon: ICONS.idea,
    text: (
      <>
        Analyzes <Bold>10Mn+ data parameters</Bold> to identify high-potential
        investment opportunities.
      </>
    ),
  },
  {
    icon: ICONS.folder,
    text: (
      <>
        Uses <Bold>10 years</Bold> of fundamental data and <Bold>18 years</Bold> of
        technical data for comprehensive stock analysis.
      </>
    ),
  },
  {
    icon: ICONS.dashboard,
    text: (
      <>
        Leverages <Bold>3 proprietary sentiment engines</Bold> to understand market
        trends and investor sentiment.
      </>
    ),
  },
  {
    icon: ICONS.omnichannel,
    text: "Designed for HNIs & UHNIs seeking a professionally managed equity portfolio.",
  },
  {
    icon: ICONS.ideaAlt,
    text: (
      <>
        Creates and optimizes your portfolio with{" "}
        <Bold>AI-driven stock selection and allocation.</Bold>
      </>
    ),
  },
  {
    icon: ICONS.robustness,
    text: (
      <>
        Actively monitors risk using a <Bold>robust risk management engine.</Bold>
      </>
    ),
  },
  {
    icon: ICONS.loading,
    text: (
      <>
        <Bold>Automatically rebalances</Bold> your portfolio as market conditions
        change.
      </>
    ),
  },
  {
    icon: ICONS.stochastic,
    text: (
      <>
        Includes a <Bold>Market Exit Indicator</Bold> for partial or complete exits{" "}
        <Bold>during market corrections or crashes.</Bold>
      </>
    ),
  },
  {
    icon: ICONS.support,
    text: (
      <>
        Ensures your investments remain aligned with evolving market conditions
        through <Bold>24 x 7 monitoring and AI-driven decision-making.</Bold>
      </>
    ),
  },
];

const MOBILE_FOR_YOU = [
  "Value personalized portfolio management.",
  "Trust AI-powered research and insights.",
  "Prefer investing without daily market monitoring.",
  "Focus on long-term wealth creation.",
  "Eligible with ₹15 lakh+ investable assets.",
  "Expect bespoke, not generic, portfolios.",
];

const MOBILE_QUICK_FACTS = [
  { label: "Investor", value: "HNIs & UHNIs" },
  { label: "Monitoring", value: "24 x 7" },
  { label: "Minimum Capital", value: "₹25 Lakh+" },
  { label: "Horizon", value: "1-5 Years" },
  { label: "Monitoring", value: "Actively" },
  { label: "Execution", value: "2 Click" },
];

const MOBILE_ABOUT_PREVIEW = 3;
const DOT_GOLD = "#DB9C0B";
const FOR_YOU_GRADIENT = "linear-gradient(134deg, #6A4F13 0%, #E8B43D 100%)";

const pagePad = {
  px: { xs: 2.5, md: 7.5 },
  maxWidth: 1320,
  mx: "auto",
  width: "100%",
};

const FeatureCard = ({ icon, text, gold }) => (
  <Stack
    spacing={1.5}
    sx={{
      p: 3,
      height: "100%",
      borderRadius: "16px",
      bgcolor: gold ? GOLD_SOFT : "#fff",
      border: "1px solid #E5E7EB",
      boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
    }}
  >
    <Box
      component="img"
      src={icon}
      alt=""
      sx={{
        width: 28,
        height: 28,
        filter: gold ? "brightness(0) invert(1)" : undefined,
      }}
    />
    <Text sx={{ fontSize: 15, fontWeight: 500, color: "#111827", lineHeight: 1.45 }}>
      {text}
    </Text>
  </Stack>
);

const ChecklistCard = ({ label, description }) => (
  <Stack
    spacing="12px"
    sx={{
      p: "24px",
      height: "100%",
      minWidth: 0,
      boxSizing: "border-box",
      borderRadius: "12px",
      bgcolor: "#fff",
      border: "1px solid #E8E2D9",
      boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
      position: "relative",
      overflow: "hidden",
    }}
  >
    <Box
      sx={{
        position: "absolute",
        top: 0,
        left: -1,
        right: -1,
        height: 3,
        bgcolor: "#D0A135",
      }}
    />
    <Stack direction="row" spacing="12px" alignItems="center" sx={{ width: "100%" }}>
      <Box
        sx={{
          width: 36,
          height: 36,
          flexShrink: 0,
          borderRadius: "12px",
          bgcolor: "rgba(208,161,53,0.05)",
          border: "1px solid rgba(208,161,53,0.2)",
          display: "grid",
          placeItems: "center",
        }}
      >
        <Box
          component="img"
          src={ICONS.check}
          alt=""
          sx={{ width: 18, height: 18, display: "block" }}
        />
      </Box>
      <Text
        sx={{
          flex: 1,
          minWidth: 0,
          fontSize: 15,
          fontWeight: 600,
          color: "#374151",
          lineHeight: "20px",
          fontFamily: "'DM Sans', sans-serif",
        }}
      >
        {label}
      </Text>
    </Stack>
    <Text
      sx={{
        fontSize: 13,
        fontWeight: 400,
        color: "#313131",
        lineHeight: "20px",
        fontFamily: "'DM Sans', sans-serif",
        width: "100%",
      }}
    >
      {description}
    </Text>
  </Stack>
);

const FactCard = ({ label, value }) => (
  <Stack
    spacing={1.25}
    sx={{
      p: 3,
      borderRadius: "16px",
      bgcolor: "#fff",
      border: "1px solid #EEE7DA",
      boxShadow: "0 4px 12px rgba(0,0,0,0.04)",
    }}
  >
    <Stack direction="row" spacing={1} alignItems="center">
      <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: GOLD }} />
      <Text sx={{ fontSize: 14, color: "#6B7280" }}>{label}</Text>
    </Stack>
    <Text sx={{ fontSize: 20, fontWeight: 700, color: "#111827" }}>{value}</Text>
  </Stack>
);

const MobileAboutRow = ({ icon, text }) => (
  <Stack direction="row" spacing={1.5} alignItems="flex-start">
    <Box
      component="img"
      src={icon}
      alt=""
      sx={{ width: 20, height: 20, flexShrink: 0, mt: "2px" }}
    />
    <Text sx={{ fontSize: 14, color: "#475569", lineHeight: "21px" }}>{text}</Text>
  </Stack>
);

const MobileForYouRow = ({ text }) => (
  <Stack
    direction="row"
    spacing={2}
    alignItems="center"
    sx={{
      p: 2,
      minHeight: 63,
      borderRadius: "16px",
      bgcolor: "rgba(255,255,255,0.12)",
      border: "1px solid rgba(255,255,255,0.12)",
      boxShadow: "0 10px 12px rgba(0,0,0,0.1)",
      backdropFilter: "blur(7px)",
    }}
  >
    <Box
      component="img"
      src={ICONS.forYouDot}
      alt=""
      sx={{ width: 24, height: 24, flexShrink: 0, display: "block" }}
    />
    <Text sx={{ fontSize: 14, fontWeight: 500, color: "#fff", lineHeight: 1.35 }}>
      {text}
    </Text>
  </Stack>
);

const MobileFactTile = ({ label, value }) => (
  <Stack
    spacing={1.25}
    sx={{
      flex: 1,
      minWidth: 0,
      p: 2,
      borderRadius: "16px",
      bgcolor: "#fff",
      border: "1px solid #E2E8F0",
      boxShadow: "0 4px 6px rgba(0,0,0,0.04)",
    }}
  >
    <Stack direction="row" spacing={1.25} alignItems="center">
      <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: DOT_GOLD }} />
      <Text sx={{ fontSize: 12, color: "#000", lineHeight: "16px" }}>{label}</Text>
    </Stack>
    <Text sx={{ fontSize: 16, fontWeight: 700, color: "#0F172A", lineHeight: "24px" }}>
      {value}
    </Text>
  </Stack>
);

const PrimeProductInfo = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const [plans, setPlans] = useState([]);
  const [plansLoading, setPlansLoading] = useState(true);
  const [aboutExpanded, setAboutExpanded] = useState(false);

  useEffect(() => {
    dispatch(jarvisPrimeOnboardingActions.getOnboardingData());
    setPlansLoading(true);
    thirdpartyApis
      .getSubscriptionPlans({ productCode: config.productCodes.PRIME })
      .then((res) => setPlans(thirdpartyParsers.subscriptionPlans(res) || []))
      .catch(() => setPlans([]))
      .finally(() => setPlansLoading(false));
  }, [dispatch]);

  const handleStartInvesting = () => {
    localStore.setActiveProduct(config.productCodes.PRIME);
    dispatch(onboardingActions.updateOnboardingData({ planId: null }));
    history.push(ONBOARDING_PATH);
  };

  const handleBack = () => {
    history.push(UNIFIED_DASHBOARD_PATH);
  };

  const aboutPreview = MOBILE_ABOUT_ITEMS.slice(0, MOBILE_ABOUT_PREVIEW);
  const aboutRest = MOBILE_ABOUT_ITEMS.slice(MOBILE_ABOUT_PREVIEW);

  return (
    <Box sx={{ bgcolor: "#fff" }}>
      {/* Hero */}
      <Box
        sx={{
          position: "relative",
          background: HERO_GRADIENT,
          overflow: "hidden",
          borderRadius: { xs: "0 0 24px 24px", md: 0 },
        }}
      >
        <Box
          sx={{
            display: { xs: "none", md: "block" },
            position: "relative",
            height: 640,
            maxWidth: 1440,
            mx: "auto",
            boxSizing: "border-box",
          }}
        >
          {/* Soft left glow */}
          <Box
            sx={{
              position: "absolute",
              left: -207,
              top: -132,
              width: 820,
              height: 820,
              borderRadius: "50%",
              background:
                "radial-gradient(circle, rgba(232,165,12,0.16) 0%, rgba(25,20,11,0) 70%)",
              pointerEvents: "none",
            }}
          />

          {/* Brand row */}
          <Stack
            direction="row"
            alignItems="center"
            spacing={1.5}
            sx={{ position: "absolute", left: 60, top: 32, zIndex: 2 }}
          >
            <IconButton
              onClick={handleBack}
              aria-label="Back to Explore"
              sx={{
                width: 42,
                height: 42,
                borderRadius: "12px",
                bgcolor: "rgba(255,255,255,0.2)",
                border: "1px solid #C3C3C3",
                boxShadow: "0 4px 6px -1px rgba(0,0,0,0.1)",
                p: 0,
                "&:hover": { bgcolor: "rgba(255,255,255,0.35)" },
              }}
            >
              <ArrowBackRoundedIcon sx={{ fontSize: 24, color: "#fff" }} />
            </IconButton>
            <Stack spacing={1.1}>
              <Text
                sx={{
                  fontSize: 32,
                  fontWeight: 800,
                  color: "#fff",
                  lineHeight: "26px",
                  fontFamily: "'DM Sans', sans-serif",
                }}
              >
                Jarvis Prime
              </Text>
              <Text
                sx={{
                  fontSize: 14,
                  fontWeight: 400,
                  color: "rgba(255,255,255,0.7)",
                  lineHeight: "20px",
                  fontFamily: "'DM Sans', sans-serif",
                }}
              >
                Investing powered by AI
              </Text>
            </Stack>
          </Stack>

          <Stack
            spacing="20px"
            sx={{
              position: "absolute",
              left: 60,
              top: 141,
              width: 651,
              zIndex: 2,
            }}
          >
            <Box sx={{ width: 644 }}>
              <Text
                sx={{
                  display: "block",
                  fontSize: 64,
                  fontWeight: 800,
                  color: "#fff",
                  lineHeight: "72px",
                  fontFamily: "'DM Sans', sans-serif",
                  whiteSpace: "nowrap",
                }}
              >
                Hyper Personalized
              </Text>
              <Text
                sx={{
                  display: "block",
                  fontSize: 64,
                  fontWeight: 800,
                  color: "#fff",
                  lineHeight: "72px",
                  fontFamily: "'DM Sans', sans-serif",
                  whiteSpace: "nowrap",
                }}
              >
                Portfolio Management
              </Text>
            </Box>

            <Box sx={{ width: 651 }}>
              {HERO_SUBHEAD_LINES.map((line) => (
                <Text
                  key={line}
                  sx={{
                    display: "block",
                    fontSize: 16,
                    fontWeight: 400,
                    color: "rgba(255,255,255,0.72)",
                    lineHeight: "26px",
                    fontFamily: "'DM Sans', sans-serif",
                    whiteSpace: "nowrap",
                  }}
                >
                  {line}
                </Text>
              ))}
            </Box>

            <Button
              onClick={handleStartInvesting}
              endIcon={<ArrowForwardRoundedIcon sx={{ fontSize: 24 }} />}
              sx={{
                alignSelf: "flex-start",
                height: 60,
                px: "28px",
                py: "18px",
                borderRadius: "14px",
                textTransform: "none",
                fontWeight: 700,
                fontSize: 16,
                color: "#fff",
                background: HERO_CTA,
                border: "1px solid rgba(255,255,255,0.3)",
                boxShadow: "0 10px 12px rgba(232,165,12,0.2)",
                "&:hover": { background: HERO_CTA, opacity: 0.95 },
                "& .MuiButton-endIcon": { ml: "12px", mr: 0 },
              }}
            >
              Start Investing
            </Button>

            <Stack
              direction="row"
              alignItems="center"
              spacing={2}
              sx={{
                width: "100%",
                height: 60,
                px: "20px",
                py: "16px",
                boxSizing: "border-box",
                borderRadius: "16px",
                bgcolor: "rgba(255,255,255,0.04)",
                border: "1px solid rgba(255,255,255,0.12)",
                boxShadow: "0 12px 24px rgba(0,0,0,0.2)",
                backdropFilter: "blur(8px)",
              }}
            >
              {HERO_BENEFITS.map((item, index) => (
                <React.Fragment key={item.label}>
                  {index > 0 ? (
                    <Box
                      sx={{
                        width: "1px",
                        height: 28,
                        bgcolor: "rgba(255,255,255,0.2)",
                        flexShrink: 0,
                      }}
                    />
                  ) : null}
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    sx={{ flex: 1, minWidth: 0 }}
                  >
                    <Box
                      sx={{
                        width: 28,
                        height: 28,
                        flexShrink: 0,
                        borderRadius: "14px",
                        bgcolor: "#FFF5EB",
                        display: "grid",
                        placeItems: "center",
                      }}
                    >
                      <Box
                        component="img"
                        src={item.icon}
                        alt=""
                        sx={{ width: 16, height: 16, display: "block" }}
                      />
                    </Box>
                    <Text
                      sx={{
                        fontSize: 11,
                        fontWeight: 500,
                        color: "#F5EFE4",
                        lineHeight: "14px",
                        fontFamily: "'DM Sans', sans-serif",
                      }}
                    >
                      {item.label}
                    </Text>
                  </Stack>
                </React.Fragment>
              ))}
            </Stack>
          </Stack>

          <Box
            sx={{
              position: "absolute",
              right: 24,
              top: 80,
              width: 620,
              height: 480,
              zIndex: 1,
            }}
          >
            <Box
              sx={{
                position: "absolute",
                left: 150,
                top: 179,
                width: 320,
                height: 320,
                borderRadius: "50%",
                background:
                  "radial-gradient(circle, rgba(232,165,12,0.28) 0%, rgba(25,20,11,0) 70%)",
                pointerEvents: "none",
              }}
            />
            <Box
              component="img"
              src={ICONS.crown}
              alt="Jarvis Prime"
              sx={{
                position: "absolute",
                left: 37,
                top: 57,
                width: 546,
                height: 423,
                objectFit: "contain",
                display: "block",
                filter: "drop-shadow(0 18px 40px rgba(0,0,0,0.35))",
              }}
            />
          </Box>
        </Box>

        {/* Mobile hero */}
        <Box sx={{ display: { xs: "block", md: "none" }, px: 2.5, pt: 2.5, pb: 0 }}>
          <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2.5 }}>
            <IconButton
              onClick={handleBack}
              aria-label="Back to Explore"
              sx={{
                width: 36,
                height: 36,
                borderRadius: "12px",
                bgcolor: "rgba(255,255,255,0.2)",
                border: "1px solid #C3C3C3",
                p: 0,
                "&:hover": { bgcolor: "rgba(255,255,255,0.35)" },
              }}
            >
              <ArrowBackRoundedIcon sx={{ fontSize: 20, color: "#fff" }} />
            </IconButton>
            <Stack spacing={0.5}>
              <Text sx={{ fontSize: 18, fontWeight: 800, color: "#fff", lineHeight: "22px" }}>
                Jarvis Prime
              </Text>
              <Text sx={{ fontSize: 14, color: "rgba(255,255,255,0.7)", lineHeight: "20px" }}>
                Investing powered by AI
              </Text>
            </Stack>
          </Stack>

          <Stack spacing={1.25}>
            <Text sx={{ fontSize: 30, fontWeight: 800, color: "#fff", lineHeight: 1.2 }}>
              Hyper Personalized
              <br />
              Portfolio Management
            </Text>
            <Text sx={{ fontSize: 14, color: "rgba(255,255,255,0.75)", lineHeight: 1.5 }}>
              {HERO_SUBHEAD}
            </Text>
            <Box
              sx={{
                display: "flex",
                alignItems: "flex-end",
                justifyContent: "space-between",
                gap: 1.5,
                mt: 4,
                minHeight: 160,
              }}
            >
              <Box
                component="img"
                src={ICONS.crown}
                alt="Jarvis Prime"
                sx={{
                  width: 200,
                  maxWidth: "52%",
                  objectFit: "contain",
                  alignSelf: "flex-end",
                  ml: -2,
                  mb: -2,
                }}
              />
              <Button
                onClick={handleStartInvesting}
                endIcon={<ArrowForwardRoundedIcon sx={{ fontSize: 20 }} />}
                sx={{
                  flexShrink: 0,
                  mb: 2,
                  px: 2,
                  py: 1.25,
                  borderRadius: "14px",
                  textTransform: "none",
                  fontWeight: 700,
                  fontSize: 14,
                  color: "#fff",
                  whiteSpace: "nowrap",
                  background: HERO_CTA,
                  border: "1px solid rgba(255,255,255,0.3)",
                  "&:hover": { background: HERO_CTA, opacity: 0.95 },
                }}
              >
                Start Investing
              </Button>
            </Box>
          </Stack>
        </Box>
      </Box>

      {/* About Prime — desktop */}
      <Box sx={{ ...pagePad, py: 7, display: { xs: "none", md: "block" } }}>
        <Text
          sx={{
            textAlign: "center",
            fontSize: 48,
            fontWeight: 700,
            color: "#111827",
            letterSpacing: "-0.02em",
            mt: 1.5,
            mb: 5,
          }}
        >
          About Prime
        </Text>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "2fr 1fr",
            gap: 2,
            mb: 2,
          }}
        >
          <Box
            sx={{
              p: 4,
              borderRadius: "20px",
              bgcolor: GOLD_SOFT,
              boxShadow: "0 8px 16px rgba(0,0,0,0.08)",
              minHeight: 200,
            }}
          >
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Box component="img" src={ICONS.frame} alt="" sx={{ width: 28, height: 28 }} />
              <Text
                sx={{
                  bgcolor: "#fff",
                  px: 1.5,
                  py: 0.5,
                  borderRadius: "12px",
                  fontSize: 11,
                  fontWeight: 500,
                  color: "#1F351D",
                }}
              >
                Currently AI under reinforcement stage
              </Text>
            </Stack>
            <Text
              sx={{
                mt: 1.5,
                fontSize: 80,
                fontWeight: 700,
                color: "#fff",
                lineHeight: 1,
              }}
            >
              2016
            </Text>
            <Text
              sx={{
                mt: 1,
                fontSize: 20,
                fontWeight: 500,
                color: "rgba(255,255,255,0.9)",
              }}
            >
              AI Engine live in Indian Market since
            </Text>
          </Box>
          <FeatureCard {...FEATURES[0]} />
        </Box>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr 1fr",
            gap: 2,
            mb: 2,
          }}
        >
          {FEATURES.slice(1, -2).map((item) => (
            <FeatureCard key={item.text} {...item} />
          ))}
        </Box>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 2,
          }}
        >
          {FEATURES.slice(-2).map((item) => (
            <FeatureCard key={item.text} {...item} />
          ))}
        </Box>
      </Box>

      {/* About Prime — mobile card + read more */}
      <Box sx={{ px: 2, py: 2.5, display: { xs: "block", md: "none" } }}>
        <Stack
          spacing={2}
          sx={{
            p: 2.5,
            borderRadius: "20px",
            bgcolor: "#fff",
            border: "1px solid #E2E8F0",
            boxShadow: "0 4px 6px rgba(0,0,0,0.04)",
          }}
        >
          <Text sx={{ fontSize: 18, fontWeight: 700, color: "#0F172A", lineHeight: "24px" }}>
            About Prime
          </Text>
          <Box sx={{ borderBottom: "1px solid #E2E8F0" }} />
          <Stack spacing={2.5}>
            {aboutPreview.map((item, index) => (
              <MobileAboutRow key={`about-preview-${index}`} {...item} />
            ))}
          </Stack>
          <Collapse in={aboutExpanded}>
            <Stack spacing={2.5} sx={{ pt: 2.5 }}>
              {aboutRest.map((item, index) => (
                <MobileAboutRow key={`about-rest-${index}`} {...item} />
              ))}
            </Stack>
          </Collapse>
          <Box
            component="button"
            type="button"
            onClick={() => setAboutExpanded((v) => !v)}
            sx={{
              border: 0,
              bgcolor: "transparent",
              cursor: "pointer",
              p: 0,
              alignSelf: "flex-start",
            }}
          >
            <Text sx={{ fontSize: 14, fontWeight: 500, color: "#2977EC", lineHeight: "24px" }}>
              {aboutExpanded ? "read less" : "read more"}
            </Text>
          </Box>
        </Stack>
      </Box>

      {/* Is This For You — desktop */}
      <Box
        sx={{
          display: { xs: "none", md: "block" },
          py: 8,
          px: 7.5,
          backgroundImage: `
            linear-gradient(rgba(208,161,53,0.05) 1px, transparent 1px),
            linear-gradient(90deg, rgba(208,161,53,0.05) 1px, transparent 1px)
          `,
          backgroundSize: "200px 160px",
        }}
      >
        <Stack
          direction="row"
          spacing={6}
          alignItems="center"
          sx={{ maxWidth: 1320, mx: "auto" }}
        >
          <Stack spacing={2.5} sx={{ flex: "0 1 420px" }}>
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: "50%",
                border: `1.5px solid ${GOLD}`,
                display: "grid",
                placeItems: "center",
                bgcolor: "#fff",
                fontSize: 36,
                fontWeight: 800,
                color: GOLD,
              }}
            >
              ?
            </Box>
            <Text
              sx={{
                fontSize: 48,
                fontWeight: 800,
                color: "#1A1A1A",
                lineHeight: 1.1,
              }}
            >
              Is This For You?
            </Text>
            <Text sx={{ fontSize: 16, color: "#6B7280", lineHeight: 1.55 }}>
              A quick checklist to help you decide if our AI-driven portfolio management is
              the right fit for your financial goals.
            </Text>
          </Stack>

          <Box
            sx={{
              flex: 1,
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 2,
              width: "100%",
            }}
          >
            {FOR_YOU_CARDS.map((card) => (
              <ChecklistCard key={card.label} {...card} />
            ))}
          </Box>
        </Stack>
      </Box>

      {/* Is This For You — mobile gold card */}
      <Box sx={{ px: 2, py: 1.25, display: { xs: "block", md: "none" } }}>
        <Stack
          spacing={2.5}
          sx={{
            p: 3,
            borderRadius: "24px",
            overflow: "hidden",
            position: "relative",
            border: "1px solid rgba(255,255,255,0.15)",
            background: FOR_YOU_GRADIENT,
            boxShadow:
              "0 0 18px rgba(232,180,61,0.15), 0 10px 24px -10px rgba(106,79,19,0.3), 0 18px 40px -12px rgba(0,0,0,0.15)",
          }}
        >
          <Box
            sx={{
              position: "absolute",
              top: -110,
              right: -90,
              width: 260,
              height: 260,
              borderRadius: "50%",
              background:
                "radial-gradient(circle, rgba(255,255,255,0.18) 0%, transparent 70%)",
              pointerEvents: "none",
            }}
          />
          <Stack spacing={1.25} sx={{ position: "relative" }}>
            <Text sx={{ fontSize: 18, fontWeight: 700, color: "#E6E6E6", lineHeight: "24px" }}>
              Is This For You?
            </Text>
            <Text sx={{ fontSize: 13, color: "rgba(255,255,255,0.8)", lineHeight: "18px" }}>
              A quick checklist to help you decide if this is the right fit.
            </Text>
          </Stack>
          <Box
            sx={{
              borderBottom: "1px solid rgba(255,255,255,0.2)",
              position: "relative",
            }}
          />
          <Stack spacing={1.5} sx={{ position: "relative" }}>
            {MOBILE_FOR_YOU.map((text) => (
              <MobileForYouRow key={text} text={text} />
            ))}
          </Stack>
        </Stack>
      </Box>

      {/* Quick Facts — desktop */}
      <Box sx={{ ...pagePad, py: 7, display: { xs: "none", md: "block" } }}>
        <Text
          sx={{
            textAlign: "center",
            fontSize: 36,
            fontWeight: 700,
            color: "#111827",
            mb: 4,
          }}
        >
          Quick Facts
        </Text>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr 1fr",
            gap: 2,
          }}
        >
          {QUICK_FACTS.map((fact) => (
            <FactCard key={`${fact.label}-${fact.value}`} {...fact} />
          ))}
        </Box>
      </Box>

      {/* Quick Facts — mobile 2-col card */}
      <Box sx={{ px: 2, py: 1.25, display: { xs: "block", md: "none" } }}>
        <Stack
          spacing={2.5}
          sx={{
            p: 3,
            borderRadius: "20px",
            bgcolor: "#fff",
            border: "1px solid #E2E8F0",
            boxShadow: "0 4px 6px rgba(0,0,0,0.04)",
          }}
        >
          <Text sx={{ fontSize: 18, fontWeight: 700, color: "#0F172A", lineHeight: "26px" }}>
            Quick Facts
          </Text>
          <Box sx={{ borderBottom: "1px solid #E2E8F0" }} />
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 1.5,
            }}
          >
            {MOBILE_QUICK_FACTS.map((fact) => (
              <MobileFactTile key={`${fact.label}-${fact.value}`} {...fact} />
            ))}
          </Box>
        </Stack>
      </Box>

      {/* Pricing */}
      <Box
        sx={{
          ...pagePad,
          py: { xs: 2.5, md: 10 },
          bgcolor: { xs: "#fff", md: "#F8FAFC" },
          maxWidth: "100%",
        }}
      >
        <Box sx={{ maxWidth: 1320, mx: "auto", width: "100%" }}>
          <Box sx={{ display: { xs: "none", md: "block" } }}>
            <Text
              sx={{
                textAlign: "center",
                fontSize: 36,
                fontWeight: 700,
                color: "#111827",
                mb: 1.5,
              }}
            >
              Pricing
            </Text>
            <Text
              sx={{
                textAlign: "center",
                fontSize: 20,
                color: "#6B7280",
                mb: 5,
              }}
            >
              Choose the plan that fits your investing style
            </Text>
          </Box>
          <PrimePricingSection
            plans={plans}
            loading={plansLoading}
            onUnlock={handleStartInvesting}
          />
        </Box>
      </Box>

      <AllInOneFooter />
    </Box>
  );
};

export default PrimeProductInfo;
